import type { ReactElement } from 'react';
import type { RequestForQuoteSummary } from '../../domain/types';
import { normalizeRfqStatus } from '../../domain/requestForQuote';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';
import { rfqStatusLabel } from '../panels/RfqWorkflowPanel';

const RFQ_COLUMNS: GroupedColumnDef[] = [
  { id: 'rfqNumber', label: 'RFQ #', header: 'RFQ #' },
  { id: 'requestNumber', label: 'Req #', header: 'Req #' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'updated', label: 'Updated', header: 'Updated' },
  { id: 'actions', label: 'Actions', header: 'Actions' }
];

function rfqGroupValue(row: RequestForQuoteSummary, columnId: string): string {
  switch (columnId) {
    case 'rfqNumber':
      return row.rfqNumber;
    case 'requestNumber':
      return row.requestNumber;
    case 'region':
      return row.region;
    case 'status':
      return rfqStatusLabel(row.status);
    case 'updated':
      return new Date(row.updatedAtUtc).toLocaleString();
    case 'actions':
      return '—';
    default:
      return '—';
  }
}

interface RfqGridProps {
  rows: RequestForQuoteSummary[];
  selectedRfqId: string | null;
  onSelectRfq: (id: string) => void;
  onOpenRfqInPage?: (id: string) => void;
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function RfqGrid({
  rows,
  selectedRfqId,
  onSelectRfq,
  onOpenRfqInPage,
  loading,
  page: currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}: RfqGridProps): ReactElement {
  function renderRow(row: RequestForQuoteSummary): ReactElement {
    const st = normalizeRfqStatus(row.status);
    const canEditFlow = st === 'draft' || st === 'rejected';
    return (
      <tr
        className={selectedRfqId === row.id ? 'selected' : undefined}
        onClick={() => onSelectRfq(row.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onOpenRfqInPage?.(row.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectRfq(row.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td>{row.rfqNumber}</td>
        <td>{row.requestNumber}</td>
        <td>{row.region}</td>
        <td>
          <span className={`status-pill rfq-status-${st.replace(/\s+/g, '')}`}>
            {rfqStatusLabel(row.status)}
          </span>
        </td>
        <td>{new Date(row.updatedAtUtc).toLocaleString()}</td>
        <td
          onClick={(e) => {
            e.stopPropagation();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {canEditFlow && onOpenRfqInPage ? (
            <button
              type="button"
              className="secondary"
              onClick={() => onOpenRfqInPage(row.id)}
            >
              Edit vendors &amp; requirements
            </button>
          ) : (
            <span className="hint">—</span>
          )}
        </td>
      </tr>
    );
  }

  return (
    <section className="card requests-grid-card">
      <h2>Request for quotes</h2>
      <p className="hint">
        Click a row to work the RFQ in the side panel; double-click to open the full page (requirements, vendors,
        approvals).
      </p>
      {loading && <p className="hint">Loading quote requests…</p>}
      <GroupedDataGrid<RequestForQuoteSummary>
        columns={RFQ_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={rfqGroupValue}
        renderRow={renderRow}
        colSpan={6}
        emptyMessage="No quote requests yet."
        groupHint="Click a column title to sort. Drag the grip beside a title into the strip to group."
      />
      <GridPaginationBar
        idPrefix="rfq-grid"
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
